/**
 * Full-page Custom Tools management (replaces chat main area when ?view=custom-tools).
 * Form/Import panels render inline and push the tools list over.
 */

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Wrench,
  Plus,
  FileDown,
  Loader2,
  Zap,
  Globe,
} from 'lucide-react'
import {
  fetchCustomTools,
  deleteCustomTool,
  updateCustomTool,
  testCustomTool,
  selectCustomTools,
  selectCustomToolsStatus,
} from '../../../features/openai/openaiSlice'
import { toast } from 'react-toastify'
import CustomToolForm from './CustomToolForm'
import ImportSpecModal from './ImportSpecModal'
import { CustomToolRowMenu, CustomToolToggle } from './CustomToolsListControls'
import { customToolHttpMethodColors } from './customToolShared'

type PanelMode = 'none' | 'form' | 'import'

const CustomToolsMainView: React.FC = () => {
  const dispatch = useDispatch()
  const items = useSelector(selectCustomTools)
  const status = useSelector(selectCustomToolsStatus)
  const [panelMode, setPanelMode] = useState<PanelMode>('none')
  const [editing, setEditing] = useState<Record<string, any> | null>(null)

  useEffect(() => {
    dispatch(fetchCustomTools() as any)
  }, [dispatch])

  const openForm = (tool: Record<string, any> | null = null) => {
    setEditing(tool)
    setPanelMode('form')
  }

  const openImport = () => setPanelMode('import')

  const closePanel = () => {
    setPanelMode('none')
    setEditing(null)
  }

  const handleToggleEnabled = async (tool: any) => {
    try {
      await dispatch(updateCustomTool({ toolId: tool.id, body: { is_enabled: !tool.is_enabled } }) as any).unwrap()
      await dispatch(fetchCustomTools() as any)
    } catch (e: any) {
      toast.error(e?.error || 'Update failed')
    }
  }

  const handleDelete = async (tool: any) => {
    if (!window.confirm(`Delete custom tool "${tool.name}"?`)) return
    try {
      await dispatch(deleteCustomTool(tool.id) as any).unwrap()
      toast.success('Tool deleted')
      await dispatch(fetchCustomTools() as any)
    } catch (e: any) {
      toast.error(e?.error || 'Delete failed')
    }
  }

  const handleTest = async (tool: any) => {
    try {
      const res = await dispatch(testCustomTool({ toolId: tool.id, args: {} }) as any).unwrap()
      if (res.ok) toast.success(`Test OK — HTTP ${res.status_code}`)
      else toast.error(res.error || `HTTP ${res.status_code}`)
    } catch (e: any) {
      toast.error(e?.error || 'Test failed')
    }
  }

  const hasSidePanel = panelMode !== 'none'

  return (
    <div className="tw-flex tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-bg-white">
      {/* Left: tools list */}
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-min-h-0 tw-overflow-hidden">
        {/* Header */}
        <div className="tw-shrink-0 tw-px-6 tw-pt-6 tw-pb-5 tw-border-b tw-border-gray-100">
          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
            <div className="tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-indigo-600 tw-p-2.5 tw-shadow-sm">
              <Wrench className="tw-w-5 tw-h-5 tw-text-white" />
            </div>
            <div>
              <h1 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">Custom Tools</h1>
              <p className="tw-text-sm tw-text-gray-500 tw-m-0">
                Connect external APIs your AI agent can call in Agent mode.
              </p>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-mt-4">
            <button
              onClick={() => openForm(null)}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors"
            >
              <Plus className="tw-w-4 tw-h-4" />
              Add tool
            </button>
            <button
              onClick={openImport}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-lg tw-bg-white tw-text-gray-700 tw-text-sm tw-font-medium tw-border tw-border-gray-200 tw-cursor-pointer tw-shadow-sm hover:tw-bg-gray-50 tw-transition-colors"
            >
              <FileDown className="tw-w-4 tw-h-4" />
              Import from OpenAPI
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-px-6 tw-py-5">
          {status === 'loading' && items.length === 0 ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
              <Loader2 className="tw-w-6 tw-h-6 tw-text-blue-500 tw-animate-spin" />
              <p className="tw-text-sm tw-text-gray-400 tw-mt-3">Loading tools...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="tw-rounded-2xl tw-border tw-border-dashed tw-border-gray-200 tw-bg-gray-50/50 tw-py-16 tw-px-8 tw-text-center">
              <div className="tw-mx-auto tw-w-12 tw-h-12 tw-rounded-xl tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-mb-4">
                <Zap className="tw-w-6 tw-h-6 tw-text-gray-400" />
              </div>
              <p className="tw-text-gray-900 tw-font-medium tw-mb-1">No tools yet</p>
              <p className="tw-text-sm tw-text-gray-500 tw-mb-5 tw-max-w-sm tw-mx-auto">
                Add an API endpoint or import from an OpenAPI spec to get started.
              </p>
              <button
                onClick={() => openForm(null)}
                className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-bg-blue-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-shadow-sm hover:tw-bg-blue-700 tw-transition-colors"
              >
                <Plus className="tw-w-4 tw-h-4" />
                Add your first tool
              </button>
            </div>
          ) : (
            <div className="tw-space-y-2">
              {items.map((tool: any) => {
                const methodClass =
                  customToolHttpMethodColors[tool.http_method?.toUpperCase()] || customToolHttpMethodColors.GET
                return (
                  <div
                    key={tool.id}
                    className={`tw-group tw-rounded-xl tw-border tw-bg-white tw-px-4 tw-py-3.5 tw-transition-all hover:tw-shadow-sm ${
                      tool.is_enabled ? 'tw-border-gray-200 hover:tw-border-gray-300' : 'tw-border-gray-100 tw-opacity-60'
                    }`}
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <div className="tw-hidden sm:tw-flex tw-mt-0.5 tw-rounded-lg tw-bg-gray-50 tw-p-2 tw-flex-shrink-0">
                        <Globe className="tw-w-4 tw-h-4 tw-text-gray-400" />
                      </div>
                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                          <span className="tw-font-medium tw-text-gray-900 tw-text-sm">{tool.name}</span>
                          <span className={`tw-inline-flex tw-items-center tw-rounded-md tw-px-1.5 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-ring-1 tw-ring-inset ${methodClass}`}>
                            {tool.http_method?.toUpperCase()}
                          </span>
                        </div>
                        <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0 tw-truncate tw-font-mono">
                          {tool.endpoint_url}
                        </p>
                        {tool.description && (
                          <p className="tw-text-sm tw-text-gray-500 tw-mt-1.5 tw-mb-0 tw-line-clamp-2">{tool.description}</p>
                        )}
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
                        <CustomToolToggle checked={!!tool.is_enabled} onChange={() => handleToggleEnabled(tool)} />
                        <CustomToolRowMenu
                          onEdit={() => openForm(tool)}
                          onTest={() => handleTest(tool)}
                          onDelete={() => handleDelete(tool)}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right: inline panel (pushes content over) */}
      {hasSidePanel && (
        <>
          <div className="tw-w-px tw-bg-gray-200 tw-shrink-0" />
          <div className="tw-w-[400px] tw-shrink-0 tw-min-h-0 tw-overflow-hidden">
            {panelMode === 'form' && (
              <CustomToolForm onClose={closePanel} editing={editing} />
            )}
            {panelMode === 'import' && (
              <ImportSpecModal onClose={closePanel} />
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default CustomToolsMainView
